<template>
  <Layout>
    <div class="row mt-md-n3" v-if="userData.permissions['view_dashboard'] == 1">
      <div class="col-xl-12">
        <!-- Dashboard Header -->
        <div class="dashboard-header mb-2 p-3 rounded">
          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <h4 class="mb-1 text-white">
                <i class="mdi mdi-view-dashboard"></i> Dashboard
              </h4>
              <p class="text-white-50 mb-0">
                <i class="mdi mdi-calendar"></i> {{ dateRange }} Analysis
              </p>
            </div>
            <div class="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
              <div class="mr-2">
                <label class="text-white-50 small">From</label>
                <input 
                  class="form-control form-control-sm bg-white border-0" 
                  v-model="date_from" 
                  type="date"
                />
              </div>
              <div class="mx-1">
                <label class="text-white-50 small">To</label>
                <input 
                  class="form-control form-control-sm bg-white border-0" 
                  v-model="date_to" 
                  type="date"
                />
              </div>
              <div class="mt-2 mt-sm-0 ml-1">
                <b-button 
                  variant="light" 
                  size="sm" 
                  @click="filtered"
                  class="px-4 mt-4"
                >
                  <i class="mdi mdi-filter"></i> Filter
                </b-button>
                <b-button 
                  variant="outline-light" 
                  size="sm" 
                  @click="refreshAllData"
                  class="ml-1 mt-4"
                  title="Refresh Data"
                >
                  <i class="mdi mdi-refresh"></i>
                </b-button>
                <b-button 
                  variant="outline-light" 
                  size="sm" 
                  @click="exportData"
                  class="ml-1 mt-4"
                  title="Export Data"
                >
                  <i class="mdi mdi-download"></i>
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Stats Cards -->
        <div class="row ">
          <div class="col-sm-6 col-xl-3">
            <div class="card stat-card">
              <div class="card-body">
                <div class="d-flex align-items-center gap-2">
                  <div class="stat-icon bg-primary-soft rounded-circle mr-3">
                    <i class="mdi mdi-account-switch text-primary"></i>
                  </div>
                  <div class="flex-grow-1">
                    <p class="text-muted mb-1 small font-weight-bold">PATIENT VISITS</p>
                    <h3 class="mb-0 font-weight-bold">{{ commas(visits) }}</h3>
                    <small class="text-success" v-if="visits > 0">
                      <i class="mdi mdi-arrow-up"></i> Active
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-sm-6 col-xl-3">
            <div class="card stat-card">
              <div class="card-body">
                <div class="d-flex align-items-center gap-2">
                  <div class="stat-icon bg-success-soft rounded-circle mr-3">
                    <i class="mdi mdi-credit-card-scan text-success"></i>
                  </div>
                  <div class="flex-grow-1">
                    <p class="text-muted mb-1 small font-weight-bold">SERVICE SALES</p>
                    <h3 class="mb-0 font-weight-bold">{{ commas(sales) }}</h3>
                    <small class="text-muted">UGX</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-sm-6 col-xl-3">
            <div class="card stat-card">
              <div class="card-body">
                <div class="d-flex align-items-center gap-2">
                  <div class="stat-icon bg-info-soft rounded-circle mr-3">
                    <i class="mdi mdi-cash-multiple text-info"></i>
                  </div>
                  <div class="flex-grow-1">
                    <p class="text-muted mb-1 small font-weight-bold">SERVICE PAYMENTS</p>
                    <h3 class="mb-0 font-weight-bold">{{ commas(payments) }}</h3>
                    <small class="text-muted">UGX</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-sm-6 col-xl-3">
            <div class="card stat-card">
              <div class="card-body">
                <div class="d-flex align-items-center gap-2">
                  <div class="stat-icon bg-warning-soft rounded-circle mr-3">
                    <i class="mdi mdi-package-variant text-warning"></i>
                  </div>
                  <div class="flex-grow-1">
                    <p class="text-muted mb-1 small font-weight-bold">EXPENSES</p>
                    <h3 class="mb-0 font-weight-bold">{{ commas(expenses) }}</h3>
                    <small class="text-muted">UGX</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Net Profit & Summary Row -->
        <div class="row mt-n2">
          <div class="col-sm-6">
            <div class="card stat-card bg-success text-white">
              <div class="card-body">
                <div class="d-flex align-items-center gap-2">
                  <div class="stat-icon bg-white-soft rounded-circle mr-3">
                    <i class="mdi mdi-chart-line text-success"></i>
                  </div>
                  <div>
                    <p class="text-white-50 mb-1 small font-weight-bold">NET PROFIT</p>
                    <h3 class="mb-0 text-white font-weight-bold">{{ commas(net_profit) }} <small>UGX</small></h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="card stat-card border-left-primary">
              <div class="card-body">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <p class="text-muted mb-1 small font-weight-bold">QUICK STATS</p>
                    <div class="d-flex flex-wrap">
                      <div class="mr-3">
                        <small class="text-muted">Avg Sale</small>
                        <h6 class="mb-0">{{ commas(getAvgSale()) }}</h6>
                      </div>
                      <div class="mr-3">
                        <small class="text-muted">Unique Patients</small>
                        <h6 class="mb-0">{{ commas(getUniquePatients()) }}</h6>
                      </div>
                      <div class="mr-3">
                        <small class="text-muted">Conversion</small>
                        <h6 class="mb-0">{{ getConversionRate() }}%</h6>
                      </div>
                      <div>
                        <small class="text-muted">Avg Visits</small>
                        <h6 class="mb-0">{{ commas(monthlyAverageVisits) }}</h6>
                      </div>
                    </div>
                  </div>
                  <div class="stat-icon bg-primary-soft rounded-circle">
                    <i class="mdi mdi-chart-bar text-primary"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Comparison Section -->
        <div class="row mt-n2" v-if="comparisonData">
          <div class="col-sm-12">
            <div class="card chart-card">
              <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="mdi mdi-compare text-primary"></i> 
                  Period Comparison
                </h5>
                <span class="badge badge-primary-soft">Last 12 Months</span>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="comparison-card p-2 rounded">
                      <h6 class="text-muted mb-2">Current Period</h6>
                      <div class="d-flex justify-content-between">
                        <span>{{ getComparisonPeriod('current', 'start') }} - {{ getComparisonPeriod('current', 'end') }}</span>
                        <span class="font-weight-bold">{{ commas(getComparisonPeriod('current', 'visits')) }} visits</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Sales</span>
                        <span class="font-weight-bold">{{ commas(getComparisonPeriod('current', 'sales')) }} UGX</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="comparison-card p-2 rounded">
                      <h6 class="text-muted mb-2">Previous Period</h6>
                      <div class="d-flex justify-content-between">
                        <span>{{ getComparisonPeriod('previous', 'start') }} - {{ getComparisonPeriod('previous', 'end') }}</span>
                        <span class="font-weight-bold">{{ commas(getComparisonPeriod('previous', 'visits')) }} visits</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Sales</span>
                        <span class="font-weight-bold">{{ commas(getComparisonPeriod('previous', 'sales')) }} UGX</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row mt-3">
                  <div class="col-md-6">
                    <div class="change-indicator" :class="getChangeClass(getChangeValue('visits'))">
                      <i :class="getChangeIcon(getChangeValue('visits'))"></i>
                      Visits: {{ getChangeValue('visits') }}% change
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="change-indicator" :class="getChangeClass(getChangeValue('sales'))">
                      <i :class="getChangeIcon(getChangeValue('sales'))"></i>
                      Sales: {{ getChangeValue('sales') }}% change
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Best & Worst Months -->
        <div class="row mt-n2" v-if="bestMonth || worstMonth">
          <div class="col-sm-6">
            <div class="card chart-card border-left-success" v-if="bestMonth">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="stat-icon bg-success-soft rounded-circle mr-3">
                    <i class="mdi mdi-trophy text-success"></i>
                  </div>
                  <div>
                    <p class="text-muted mb-1 small font-weight-bold">BEST PERFORMING MONTH</p>
                    <h5 class="mb-0">{{ bestMonth.month }}</h5>
                    <small>Sales: {{ commas(bestMonth.sales) }} UGX | Visits: {{ bestMonth.visits }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-6">
            <div class="card chart-card border-left-danger" v-if="worstMonth">
              <div class="card-body">
                <div class="d-flex align-items-center">
                  <div class="stat-icon bg-danger-soft rounded-circle mr-3">
                    <i class="mdi mdi-arrow-down text-danger"></i>
                  </div>
                  <div>
                    <p class="text-muted mb-1 small font-weight-bold">WORST PERFORMING MONTH</p>
                    <h5 class="mb-0">{{ worstMonth.month }}</h5>
                    <small>Sales: {{ commas(worstMonth.sales) }} UGX | Visits: {{ worstMonth.visits }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Charts Section -->
        <div class="row mt-n2">
          <!-- Department Analysis -->
          <div class="col-lg-5">
            <div class="card chart-card">
              <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="mdi mdi-domain text-primary"></i> 
                  {{ currentMonthName }} Department Analysis
                </h5>
                <span class="badge badge-primary-soft">{{ currentMonthName }}</span>
              </div>
              <div class="card-body p-2">
                <apexchart
                  class="apex-charts"
                  type="bar"
                  height="300"
                  :options="monthlyservices.chartOptions"
                  :series="monthlyservices.series"
                ></apexchart>
              </div>
            </div>
          </div>

          <!-- Yearly Analysis Tabs -->
          <div class="col-lg-7">
            <div class="card chart-card">
              <div class="card-header bg-transparent border-0">
                <h5 class="mb-0">
                  <i class="mdi mdi-chart-line text-primary"></i> 
                  Yearly Analysis
                </h5>
              </div>
              <div class="card-body p-2">
                <b-tabs 
                  pills 
                  justified 
                  nav-class="p-0 mb-2" 
                  content-class="text-primary"
                  v-model="activeTab"
                >
                  <b-tab class="p-0" title="Visits">
                    <apexchart
                      class="apex-charts"
                      type="area"
                      height="260"
                      :options="monthlyvisits.chartOptions"
                      :series="monthlyvisits.series"
                    ></apexchart>
                  </b-tab>
                  <b-tab class="p-0" title="Payments">
                    <apexchart
                      class="apex-charts"
                      type="area"
                      height="260"
                      :options="monthlypayments.chartOptions"
                      :series="monthlypayments.series"
                    ></apexchart>
                  </b-tab>
                  <b-tab class="p-0" title="Pharmacy">
                    <apexchart
                      class="apex-charts"
                      type="area"
                      height="260"
                      :options="monthlypharmacys.chartOptions"
                      :series="monthlypharmacys.series"
                    ></apexchart>
                  </b-tab>
                  <b-tab class="p-0" title="Expenses">
                    <apexchart
                      class="apex-charts"
                      type="area"
                      height="260"
                      :options="monthlyexpenses.chartOptions"
                      :series="monthlyexpenses.series"
                    ></apexchart>
                  </b-tab>
                </b-tabs>
              </div>
            </div>
          </div>
        </div>

        <!-- Weekly Trends -->
        <div class="row mt-n2" v-if="weeklyStats && weeklyStats.length > 0">
          <div class="col-sm-12">
            <div class="card chart-card">
              <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="mdi mdi-calendar-week text-primary"></i> 
                  Weekly Trends
                </h5>
                <span class="badge badge-primary-soft">This Week</span>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th><i class="mdi mdi-calendar"></i> Day</th>
                        <th><i class="mdi mdi-calendar-range"></i> Date</th>
                        <th class="text-center"><i class="mdi mdi-account"></i> Visits</th>
                        <th class="text-right"><i class="mdi mdi-currency-usd"></i> Sales (UGX)</th>
                        <th class="text-center">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(day, index) in weeklyStats" :key="day.date">
                        <td class="font-weight-bold">{{ day.day }}</td>
                        <td>{{ day.date }}</td>
                        <td class="text-center">
                          <span class="badge badge-primary-soft">{{ day.visits }}</span>
                        </td>
                        <td class="text-right">{{ commas(day.sales) }}</td>
                        <td class="text-center">
                          <span v-if="index > 0">
                            <i v-if="day.sales > weeklyStats[index-1].sales" 
                               class="mdi mdi-arrow-up text-success"></i>
                            <i v-else-if="day.sales < weeklyStats[index-1].sales" 
                               class="mdi mdi-arrow-down text-danger"></i>
                            <i v-else class="mdi mdi-minus text-muted"></i>
                          </span>
                          <span v-else class="text-muted">-</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Advanced Analytics -->
        <div class="row mt-n2" v-if="advancedMetrics">
          <div class="col-sm-12">
            <div class="card chart-card">
              <div class="card-header bg-transparent border-0">
                <h5 class="mb-0">
                  <i class="mdi mdi-chart-pie text-primary"></i> 
                  Advanced Analytics
                </h5>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-6 col-md-3 mb-3 mb-md-0">
                    <div class="analytics-item text-center p-3 rounded">
                      <div class="analytics-icon bg-primary-soft rounded-circle mx-auto mb-2">
                        <i class="mdi mdi-currency-usd text-primary"></i>
                      </div>
                      <h6 class="text-muted small mb-1">AVG SALE VALUE</h6>
                      <h4 class="mb-0 font-weight-bold">{{ commas(advancedMetrics.average_sale) }}</h4>
                    </div>
                  </div>
                  <div class="col-6 col-md-3 mb-3 mb-md-0">
                    <div class="analytics-item text-center p-3 rounded">
                      <div class="analytics-icon bg-success-soft rounded-circle mx-auto mb-2">
                        <i class="mdi mdi-account-multiple text-success"></i>
                      </div>
                      <h6 class="text-muted small mb-1">UNIQUE PATIENTS</h6>
                      <h4 class="mb-0 font-weight-bold">{{ commas(advancedMetrics.unique_patients) }}</h4>
                    </div>
                  </div>
                  <div class="col-6 col-md-3 mb-3 mb-md-0">
                    <div class="analytics-item text-center p-3 rounded">
                      <div class="analytics-icon bg-info-soft rounded-circle mx-auto mb-2">
                        <i class="mdi mdi-percent text-info"></i>
                      </div>
                      <h6 class="text-muted small mb-1">CONVERSION RATE</h6>
                      <h4 class="mb-0 font-weight-bold">{{ advancedMetrics.conversion_rate }}%</h4>
                    </div>
                  </div>
                  <div class="col-6 col-md-3">
                    <div class="analytics-item text-center p-3 rounded">
                      <div class="analytics-icon bg-warning-soft rounded-circle mx-auto mb-2">
                        <i class="mdi mdi-cash text-warning"></i>
                      </div>
                      <h6 class="text-muted small mb-1">PAYMENT TRANSACTIONS</h6>
                      <h4 class="mb-0 font-weight-bold">{{ commas(advancedMetrics.payment_transactions) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading Skeleton -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2 text-muted">Loading dashboard data...</p>
        </div>
      </div>
    </div>

    <!-- No Permission -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div class="text-center p-5">
            <img src="@/assets/images/profile-img.png" alt="" class="img-fluid" style="max-width: 200px;" />
            <h5 class="text-primary mt-4">Access Restricted</h5>
            <p class="text-muted">You have no permission to view dashboard items</p>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import {
  patientMethods,
  patientComputed,
  patientpaymentMethods,
  patientpaymentComputed,
  consultationPatientMethods,
  consultationPatientComputed,
  radiologyPatientMethods,
  radiologyPatientComputed,
  antenatalPatientMethods,
  antenatalPatientComputed,
  procedurePatientMethods,
  procedurePatientComputed,
  dentalPatientMethods,
  dentalPatientComputed,
  theaterPatientMethods,
  theaterPatientComputed,
  otherPatientMethods,
  otherPatientComputed,
  maternityPatientMethods,
  maternityPatientComputed,
} from "@/state/helpers";

export default {
  page: {
    title: "Dashboard",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      dateRange: "Today",
      visits: 0,
      payments: 0,
      sales: 0,
      expenses: 0,
      net_profit: 0,
      date_from: new Date().toLocaleDateString("fr-CA"),
      date_to: new Date().toLocaleDateString("fr-CA"),
      services: [],
      yearlyvisits: [],
      yearlypharmacy: [],
      yearlypayments: [],
      yearlyexpenses: [],
      weeklyStats: [],
      advancedMetrics: null,
      comparisonData: null,
      bestMonth: null,
      worstMonth: null,
      monthlyAverageVisits: 0,
      activeTab: 0,
      loading: false,
      monthlyvisits: {
        series: [{ name: "Visits", data: [] }],
        chartOptions: {
          noData: { text: "Please Wait..." },
          theme: { monochrome: { enabled: true, color: "#004733" } },
          stroke: { curve: 'smooth' },
          fill: { type: 'gradient' },
          xaxis: { labels: { rotate: -45 } },
        },
      },
      monthlypharmacys: {
        series: [{ name: "Amount Paid", data: [] }],
        chartOptions: {
          noData: { text: "Please Wait..." },
          theme: { monochrome: { enabled: true, color: "#004733" } },
          stroke: { curve: 'smooth' },
          fill: { type: 'gradient' },
          xaxis: { labels: { rotate: -45 } },
        },
      },
      monthlypayments: {
        series: [{ name: "Amount Paid", data: [] }],
        chartOptions: {
          noData: { text: "Please Wait..." },
          theme: { monochrome: { enabled: true, color: "#004733" } },
          stroke: { curve: 'smooth' },
          fill: { type: 'gradient' },
          xaxis: { labels: { rotate: -45 } },
        },
      },
      monthlyexpenses: {
        series: [{ name: "Amount Paid", data: [] }],
        chartOptions: {
          noData: { text: "Please Wait..." },
          theme: { monochrome: { enabled: true, color: "#004733" } },
          stroke: { curve: 'smooth' },
          fill: { type: 'gradient' },
          xaxis: { labels: { rotate: -45 } },
        },
      },
      monthlyservices: {
        series: [{ name: "Patients", data: [] }],
        chartOptions: {
          noData: { text: "Please Wait..." },
          theme: { monochrome: { enabled: true, color: "#004733" } },
          plotOptions: {
            bar: { borderRadius: 4, horizontal: false },
          },
          xaxis: { labels: { rotate: -45 } },
        },
      },
      showModal: false,
      currentmonth: new Date(),
    };
  },
  computed: {
    ...patientComputed,
    ...patientpaymentComputed,
    ...antenatalPatientComputed,
    ...consultationPatientComputed,
    ...dentalPatientComputed,
    ...otherPatientComputed,
    ...procedurePatientComputed,
    ...theaterPatientComputed,
    ...radiologyPatientComputed,
    ...maternityPatientComputed,
    currentMonthName() {
      return this.currentmonth.toLocaleString("default", { month: "long" });
    },
  },
  created() {
    this.loadAllData();
  },
  methods: {
    ...patientMethods,
    ...patientpaymentMethods,
    ...antenatalPatientMethods,
    ...consultationPatientMethods,
    ...dentalPatientMethods,
    ...otherPatientMethods,
    ...procedurePatientMethods,
    ...theaterPatientMethods,
    ...radiologyPatientMethods,
    ...maternityPatientMethods,

    // Utility Methods
    commas(x) {
      if (!x) return "0";
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    getAvgSale() {
      return this.advancedMetrics ? this.advancedMetrics.average_sale : 0;
    },

    getUniquePatients() {
      return this.advancedMetrics ? this.advancedMetrics.unique_patients : 0;
    },

    getConversionRate() {
      return this.advancedMetrics ? this.advancedMetrics.conversion_rate : 0;
    },

    getComparisonPeriod(period, key) {
      if (!this.comparisonData || !this.comparisonData[period]) return '';
      return this.comparisonData[period][key] || '';
    },

    getChangeValue(type) {
      if (!this.comparisonData || !this.comparisonData.changes) return 0;
      return this.comparisonData.changes[type] || 0;
    },

    getChangeClass(value) {
      if (value > 0) return 'text-success';
      if (value < 0) return 'text-danger';
      return 'text-muted';
    },

    getChangeIcon(value) {
      if (value > 0) return 'mdi mdi-arrow-up';
      if (value < 0) return 'mdi mdi-arrow-down';
      return 'mdi mdi-minus';
    },

    // Load all data
    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetchTodaysAnalysis(),
          this.fetchYearlyAnalysis(),
          this.fetchWeeklyAnalysis(),
          this.fetchAdvancedAnalytics(),
          this.fetchComparisonData(),
          this.fetchPatients(),
          this.fetchPatientPayments(),
          this.fetchConsultationPatients(),
        ]);
      } catch (error) {
        alert("Error loading dashboard data:", error);
        this.$bvToast.toast("Error loading dashboard data", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    // Fetch Today's Analysis
    async fetchTodaysAnalysis() {
      try {
        const response = await axios.get("/todaysanalysis");
        const data = response.data;
        this.visits = data.visits || 0;
        this.sales = data.sales || 0;
        this.payments = data.payments || 0;
        this.expenses = data.expenses || 0;
        this.net_profit = data.net_profit || 0;
        this.dateRange = data.date || "Today";
      } catch (error) {
        alert("Error fetching today's analysis:", error);
      }
    },

    // Fetch Filtered Analysis
    async filtered() {
      if (!this.date_from || !this.date_to) {
        this.$bvToast.toast("Please select both date fields", {
          title: "Validation Error",
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.loading = true;
      try {
        const formData = new FormData();
        formData.append("date_from", this.date_from);
        formData.append("date_to", this.date_to);

        const response = await axios.post("/filteredanalysis", formData);
        const data = response.data;
        this.visits = data.visits || 0;
        this.sales = data.sales || 0;
        this.payments = data.payments || 0;
        this.expenses = data.expenses || 0;
        this.net_profit = data.net_profit || 0;
        this.dateRange = this.date_from + " to " + this.date_to;
        this.advancedMetrics = data;

        this.$bvToast.toast("Analysis filtered successfully", {
          title: "Success",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        alert("Error filtering analysis:", error);
        this.$bvToast.toast("Error filtering data", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    // Fetch Yearly Analysis
    async fetchYearlyAnalysis() {
      try {
        const response = await axios.get("/yearlyanalysis");
        const data = response.data;

        this.yearlyvisits = data.monthly_stats || [];
        this.yearlypayments = data.monthly_stats || [];
        this.yearlyexpenses = data.monthly_stats || [];
        this.services = data.top_services || [];
        this.bestMonth = data.best_month || null;
        this.worstMonth = data.worst_month || null;
        this.monthlyAverageVisits = data.summary?.average_visits || 0;

        const VisitData = this.yearlyvisits.map((e) => ({
          x: e.month_short || e.month,
          y: e.visits || 0,
        }));
        const PaymentData = this.yearlypayments.map((e) => ({
          x: e.month_short || e.month,
          y: e.payments || 0,
        }));
        const ExpenseData = this.yearlyexpenses.map((e) => ({
          x: e.month_short || e.month,
          y: e.expenses || 0,
        }));
        const servicesData = this.services.map((e) => ({
          x: e.name,
          y: e.count || 0,
        }));

        this.monthlyvisits.series = [{ name: "Visits", data: VisitData }];
        this.monthlypayments.series = [{ name: "Payments", data: PaymentData }];
        this.monthlyexpenses.series = [{ name: "Expenses", data: ExpenseData }];
        this.monthlyservices.series = [{ name: "Patients", data: servicesData }];

        if (data.pharmacy_sales) {
          const PharmacyData = data.pharmacy_sales.map((e) => ({
            x: e.month,
            y: e.amount || 0,
          }));
          this.monthlypharmacys.series = [
            { name: "Pharmacy Sales", data: PharmacyData },
          ];
        }

        this.fetchAntenatalPatients();
        this.fetchDentalPatients();
        this.fetchRadiologyPatients();
        this.fetchOtherPatients();
        this.fetchTheaterPatients();
        this.fetchMaternityPatients();
      } catch (error) {
        alert("Error fetching yearly analysis:", error);
      }
    },

    // Fetch Weekly Analysis
    async fetchWeeklyAnalysis() {
      try {
        const response = await axios.get("/weeklyanalysis");
        const data = response.data;
        this.weeklyStats = data.daily_stats || [];
      } catch (error) {
        alert("Error fetching weekly analysis:", error);
      }
    },

    // Fetch Advanced Analytics
    async fetchAdvancedAnalytics() {
      try {
        const response = await axios.get("/advancedanalysis", {
          params: { period: "month" },
        });
        const data = response.data;
        this.advancedMetrics = data.metrics || null;
      } catch (error) {
        alert("Error fetching advanced analytics:", error);
      }
    },

    // Fetch Comparison Data
    async fetchComparisonData() {
      try {
        const response = await axios.get("/advancedanalysis", {
          params: { period: "month" },
        });
        const data = response.data;
        this.comparisonData = data.comparison || null;
      } catch (error) {
        alert("Error fetching comparison data:", error);
      }
    },

    // Export Data
    exportData() {
      const data = {
        dateRange: this.dateRange,
        visits: this.visits,
        sales: this.sales,
        payments: this.payments,
        expenses: this.expenses,
        net_profit: this.net_profit,
        weeklyStats: this.weeklyStats,
        advancedMetrics: this.advancedMetrics,
        comparisonData: this.comparisonData,
        bestMonth: this.bestMonth,
        worstMonth: this.worstMonth,
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dashboard-data-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.$bvToast.toast("Data exported successfully", {
        title: "Success",
        variant: "success",
        solid: true,
      });
    },

    // Refresh all data
    refreshAllData() {
      this.loadAllData();
      this.$bvToast.toast("Dashboard refreshed successfully", {
        title: "Refreshed",
        variant: "success",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
/* Dashboard Header */
.dashboard-header {
  background: linear-gradient(135deg, #004733 0%, #006b4c 100%);
  box-shadow: 0 2px 10px rgba(0, 71, 51, 0.2);
}

/* Stat Cards */
.stat-card {
  border: none;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

/* Chart Cards */
.chart-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
}

/* Analytics Items */
.analytics-item {
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.analytics-item:hover {
  background: #e9ecef;
  transform: scale(1.02);
}

.analytics-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

/* Comparison Cards */
.comparison-card {
  background: #f8f9fa;
  border-left: 4px solid #004733;
}

.comparison-card:hover {
  background: #e9ecef;
}

.change-indicator {
  padding: 10px;
  border-radius: 8px;
  background: #f8f9fa;
  font-weight: 500;
  text-align: center;
}

/* Background Soft Colors */
.bg-primary-soft {
  background: rgba(0, 71, 51, 0.1);
}

.bg-success-soft {
  background: rgba(40, 167, 69, 0.1);
}

.bg-info-soft {
  background: rgba(23, 162, 184, 0.1);
}

.bg-warning-soft {
  background: rgba(255, 193, 7, 0.1);
}

.bg-danger-soft {
  background: rgba(220, 53, 69, 0.1);
}

.bg-white-soft {
  background: rgba(255, 255, 255, 0.2);
}

/* Border Left */
.border-left-primary {
  border-left: 4px solid #004733;
}

.border-left-success {
  border-left: 4px solid #28a745;
}

.border-left-danger {
  border-left: 4px solid #dc3545;
}

/* Badge Soft */
.badge-primary-soft {
  background: rgba(0, 71, 51, 0.1);
  color: #004733;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 500;
}

/* Table Improvements */
.table thead th {
  border-top: none;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.table tbody tr {
  transition: all 0.2s ease;
}

.table tbody tr:hover {
  background: #f8f9fa;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 15px !important;
  }
  
  .stat-card {
    margin-bottom: 15px;
  }
  
  .analytics-item {
    margin-bottom: 10px;
  }
}

/* Loading Animation */
.spinner-border {
  width: 40px;
  height: 40px;
}

/* Text Colors */
.text-white-50 {
  color: rgba(255, 255, 255, 0.7) !important;
}

/* Smooth Transitions */
* {
  transition: all 0.2s ease-in-out;
}
</style>